/**
 * Card - Componente de visualización de orden de ubicación en mobile
 *
 * Este componente muestra la información de una orden de ubicación en formato de tarjeta,
 * respetando las columnas seleccionadas por el usuario a través del ColumnSelector.
 * La columna 'id' es fija y siempre se muestra.
 */
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { RenderItemProps } from '@shared/components/ui/VirtualList';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LocationOrdersModel } from '../../models/LocationOrders';

// 🎯 Props para Card incluyendo las acciones
export interface CardProps extends RenderItemProps<LocationOrdersModel> {
  className?: string;
  showActions?: boolean;
  showIndex?: boolean;
  selectedColumns?: string[];
}

// 🎯 Función para obtener el color del badge según el estado
const getStatusColor = (status: string): string => {
  switch (status?.toLowerCase()) {
    case 'done':
    case 'completo':
      return 'text-success light:bg-bg-success';
    case 'pending':
    case 'pendiente':
      return 'text-badge-status-3 light:bg-badge-bg-status-3';
    case 'in_progress':
    case 'en_proceso':
      return 'text-warning light:bg-bg-warning';
    case 'cancelled':
    case 'cancelado':
      return 'text-error light:bg-bg-error';
    default:
      return 'text-badge-status-3';
  }
};

// 🎯 Función para obtener el nombre del estado
const getStatusName = (status: string): string => {
  switch (status?.toLowerCase()) {
    case 'done':
      return 'Completo';
    case 'pending':
      return 'Pendiente';
    case 'in_progress':
      return 'En proceso';
    case 'cancelled':
      return 'Cancelado';
    default:
      return status || '-';
  }
};

// 🎯 Componente de card individual para Orden de Ubicación - SRP
export const Card = ({ item: locationorder, className = '', showActions = true, selectedColumns }: CardProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation('locationordersModule');

  // 🎯 Helper para verificar si una columna está visible
  const isColumnVisible = (columnId: string) => {
    if (!selectedColumns) return true; // Si no hay selectedColumns, mostrar todo
    return selectedColumns.includes(columnId);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <div className={`py-4 px-2 border-b border-card-border hover:bg-hover-press transition-colors ${className}`}>
      <div className="space-y-3">
        {/* Header con ID - ID siempre visible */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-title">#{locationorder.id}</span>
          </div>
        </div>

        {/* Información de preparación origen y carrito */}
        {(isColumnVisible('source_id') || isColumnVisible('cart_id')) && (
          <div className="flex items-center gap-4">
            {isColumnVisible('source_id') && (
              <div className="flex flex-col">
                <span className="text-xs text-subtext">{t('table.source_id')}</span>
                <span className="text-sm text-title">{locationorder.source?.code || '-'}</span>
              </div>
            )}
            {isColumnVisible('cart_id') && (
              <div className="flex flex-col">
                <span className="text-xs text-subtext">{t('table.cart_id')}</span>
                <span className="text-sm text-title">{locationorder.cart?.code || '-'}</span>
              </div>
            )}
          </div>
        )}

        {/* Estado y fecha - Condicionales según columnas seleccionadas */}
        {(isColumnVisible('status') || isColumnVisible('created_at')) && (
          <div className="flex items-center justify-between">
            {isColumnVisible('status') && (
              <Badge
                variant="outline"
                className={`text-xs bg-card-border border-0 ${getStatusColor(locationorder.status)}`}
              >
                {getStatusName(locationorder.status)}
              </Badge>
            )}
            {isColumnVisible('created_at') && (
              <span className="text-sm text-text">{formatDate(locationorder.created_at)}</span>
            )}
          </div>
        )}

        {/* Botón de acción */}
        {showActions && (
          <Button
            variant="outline"
            className="w-full py-2 px-4 rounded-lg text-sm font-medium"
            onClick={() => {
              navigate(`/location-orders/${locationorder.id}`);
            }}
            title={t('buttons.view')}
            data-testid="view-details-button"
          >
            {t('buttons.view')}
          </Button>
        )}
      </div>
    </div>
  );
};
